// Not-billed CPT lines (design 2026-10-21, patient-supplied drugs). A code-list entry
// with `notBilled === true` stays in the note but never reaches the charge slip. Every
// consumer checks only that property; anything else means billable.
//
// Scope change 2026-10-22: the flag is only *surfaced* (the note badge and the
// "(not billed)" copy-text suffix) inside the Non-Billable Injection Administration
// section. Regular CPT lists keep the data contract and the charge-slip skip as a
// safety net, but show no billing control or badge.
//
// Kept dependency-free: utils.js, billingRules.js, codedListHtml.js and the charge-slip
// helpers all import it, and billingRules.js already imports utils.js.

export const NOT_BILLED_PATIENT_SUPPLIED = "patientSupplied";
export const NOT_BILLED_OTHER = "other";

// The Non-Billable Injection Administration section (design 2026-10-22): every row in it is
// unbilled by construction, so every FE writer re-stamps the flag (stampNonBillable in utils.js).
export const NON_BILLABLE_LIST = "nonBillableInjections";

export function isNonBillableList(variable) {
  return variable === NON_BILLABLE_LIST;
}

// Oncology biosimilars are Q-codes (Q5103 infliximab-dyyb), so this list widens the drug-line
// prefix; the other lists keep J-only so their behaviour is unchanged.
export function isDrugCodeFor(type, code) {
  const re = isNonBillableList(type) ? /^[JQ]\d/i : /^J\d/i;
  return re.test(code == null ? "" : String(code));
}

export function isNotBilled(item) {
  return item != null && typeof item === "object" && !Array.isArray(item) && item.notBilled === true;
}

// The string-text "(... not billed)" check in sanitizeCodes is opt-in and applies only to
// the CPT-style lists, never to ICD-10 dictionaries or level-of-service slots.
const CPT_LIST_SOURCES = ["cpt2codes", "inOfficeProcedures", NON_BILLABLE_LIST];

// The single list of CPT-style code-list sources (the ones that carry billable lines).
export function isCptListSource(valueSource) {
  return CPT_LIST_SOURCES.includes(valueSource);
}

export function notBilledTextApplies(valueSource) {
  return isCptListSource(valueSource);
}

// Fixed English on purpose: the chart text is pasted into English AdvancedMD charts, and
// convertHTMLToText has no translation context. One label for every reason (2026-10-27): the
// "patient-supplied" wording was dropped from the note, the copy text and Edit Codes. The stored
// notBilledReason stays in the data for the backend but is no longer shown anywhere.
export const NOT_BILLED_SUFFIX = " (not billed)";

export function notBilledSuffix(item) {
  return isNotBilled(item) ? NOT_BILLED_SUFFIX : "";
}
